import {Component, Input, OnInit} from '@angular/core';
import {BsModalRef, BsModalService} from "ngx-bootstrap/modal";
import {Subscription} from 'rxjs';
import {AuthenticatorModalComponent} from "../authenticator-modal/authenticator-modal.component";
import {ProfileService} from "../../services/profile/profile.service";
import {AlertBannerService, AlertType} from "../../lib/alert-banner";

@Component({
  selector: 'app-mfa-modal',
  templateUrl: './mfa-modal.component.pug',
  styleUrl: './mfa-modal.component.scss',
  standalone: false
})
export class MfaModalComponent implements OnInit{
  static readonly SMS = 'sms';
  static readonly APP = 'app';

  @Input() mobile = '';
  @Input() mfaConfig = {};
  @Input() orgRequiresMfa = false;

  loading = false;
  mobileVerified = false;
  isToggledSms = false;
  isToggledApp = false;
  setDefaultMobile = false;
  setDefaultApp = false;
  preferredMethod = '';
  appEnrolmentComplete = false;
  private serverAppAccepted = false;
  tooltipText = $localize`To enable, please ensure that you have a verified phone number in your personal settings.`;
  enabledText = $localize`Enabled`;
  disabledText = $localize`Disabled`;

  constructor(
    private profile: ProfileService,
    private alertList: AlertBannerService,
    private modalService: BsModalService,
    public _bsModalRef: BsModalRef
  ) {}

  ngOnInit(): void {
    this.setDefaultMobile = this.mfaConfig['preferred_method'] === MfaModalComponent.SMS;
    this.setDefaultApp = this.mfaConfig['preferred_method'] === MfaModalComponent.APP;

    this.preferredMethod = this.mfaConfig['preferred_method'];
    this.mobileVerified = this.mfaConfig['sms_verified'];
    this.isToggledSms = this.mfaConfig['sms_enabled'];
    this.isToggledApp = this.mfaConfig['app_enabled'];
    this.appEnrolmentComplete = !!this.mfaConfig['app_enabled'];
    this.serverAppAccepted = !!this.mfaConfig['app_enabled'];
  }

  smsToggle(event: any): void{
    this.isToggledSms = event;
    this.reconcilePreferredMethod();
  }

  appToggle(event: any): void{
    this.isToggledApp = event;
    if(event){
      if (this.serverAppAccepted) {
        this.appEnrolmentComplete = true;
      } else {
        this.showAuthenticatorModal(true);
      }
    } else {
      this.appEnrolmentComplete = false;
    }
    this.reconcilePreferredMethod();
  }

  cancel() {
    this._bsModalRef.hide();
  }

  save() {
    this.reconcilePreferredMethod();
    const payload = {
      'preferred_method': this.preferredMethod,
      'sms_enabled': this.isToggledSms,
      'app_enabled': this.isToggledApp,
    };
    this.loading = true;

    this.profile.updateUserMFAPreference(payload).subscribe(data => {
      this.loading = false;
      this._bsModalRef.hide();
    }, error => {
      this.loading = false;
      console.log(error);
      this.alertList.append([{
        type: AlertType.DANGER,
        message: $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`,
        keepOnRouteChange: true
      }]);
    });
  }

  setAsDefault(method: string){
    if(method=='mobile'){
      this.preferredMethod = MfaModalComponent.SMS;
      this.setDefaultMobile = true;
      this.setDefaultApp = false;
    }
    else if(method=='authenticator'){
      this.preferredMethod = MfaModalComponent.APP;
      this.setDefaultMobile = false;
      this.setDefaultApp = true;
    }
    else if(method === ''){
      this.preferredMethod = '';
      this.setDefaultMobile = false;
      this.setDefaultApp = false;
    }
  }

  private reconcilePreferredMethod(): void {
    if (!this.isToggledSms && !this.isToggledApp) {
      this.setAsDefault('');
      return;
    }
    if (this.preferredMethod === MfaModalComponent.SMS && !this.isToggledSms && this.isToggledApp) {
      this.setAsDefault('authenticator');
    } else if (this.preferredMethod === MfaModalComponent.APP && !this.isToggledApp && this.isToggledSms) {
      this.setAsDefault('mobile');
    } else if (!this.preferredMethod && this.isToggledSms) {
      this.setAsDefault('mobile');
    } else if (!this.preferredMethod && this.isToggledApp) {
      this.setAsDefault('authenticator');
    }
  }

  resetAuthentication(): void {
    this.loading = true;
    this.profile.resetAppMfa().subscribe(() => {
      this.loading = false;
      this.serverAppAccepted = false;
      this.appEnrolmentComplete = false;
      this.showAuthenticatorModal(false);
    }, () => {
      this.loading = false;
      this.alertList.append([{
        type: AlertType.DANGER,
        message: $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`,
        keepOnRouteChange: true
      }]);
    });
  }

  get isSaveDisabled(): boolean {
    if (this.orgRequiresMfa && !this.isToggledSms && !this.isToggledApp) {
      return true;
    }
    if (this.isToggledApp && !this.appEnrolmentComplete) {
      return true;
    }
    return false;
  }

  showAuthenticatorModal(revertAppToggleOnDismiss = false): void {
    this.loading = true;
    this.profile.getMfaAppQrCode().subscribe(data => {
      this.loading = false;
      let setupVerified = false;
      const subscriptions: Subscription[] = [];
      const authenticatorModalRef = this.modalService.show(AuthenticatorModalComponent, {
        class: 'modal-dialog-centered modal-authenticator',
        initialState: { qrCodeData: data.provisioning_uri },
      });
      const authenticatorModal = authenticatorModalRef.content as AuthenticatorModalComponent;

      subscriptions.push(
        authenticatorModal.verified.subscribe(({ code }) => {
          this.profile.verifyMfaApp(code).subscribe(
            (response) => {
              if (response?.app_accepted) {
                setupVerified = true;
                this.serverAppAccepted = true;
                this.appEnrolmentComplete = true;
                authenticatorModal.closeModal();
              } else {
                authenticatorModal.setError();
              }
            },
            () => authenticatorModal.setError(),
          );
        }),
      );

      if (authenticatorModalRef.onHide) {
        subscriptions.push(
          authenticatorModalRef.onHide.subscribe(() => {
            subscriptions.forEach((subscription) => subscription.unsubscribe());
            if (revertAppToggleOnDismiss && !setupVerified) {
              this.isToggledApp = false;
            }
          }),
        );
      }
    }, () => {
      this.loading = false;
      if (revertAppToggleOnDismiss) {
        this.isToggledApp = false;
      }
      this.alertList.append([{
        type: AlertType.DANGER,
        message: $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`,
        keepOnRouteChange: true
      }]);
    });
  }

}
